<template>
    <p v-if="rdkitError">Error loading renderer</p>

    <p v-else-if="!rdkitLoaded">Loading renderer</p>

    <span
        v-else-if="!isValidMolString(structure)"
        :title="`Cannot render structure: ${structure}`"
    ></span>

    <div
        v-else-if="svgMode"
        :class="`molecule-structure-svg ${className}`"
        :style="{ width: width + 'px', height: height + 'px' }"
        v-html="svg"
    ></div>

    <div v-else :class="`molecule-canvas-container ${className}`">
        <canvas
            :title="structure"
            :id="id"
            :width="width"
            :height="height"
        ></canvas>
    </div>
</template>

<script>
import { nextTick, onMounted, onUpdated, reactive, ref, watch } from "vue";
import initRDKit from "../utils/initRDKit.js";

export default {
    name: "MoleculeStructure",
    props: {
        id: { type: String, required: true },
        className: { type: String, default: "" },
        svgMode: { type: Boolean, default: false },
        width: { type: Number, default: 250 },
        height: { type: Number, default: 200 },
        structure: { type: String, required: true },
        subStructure: { type: String, default: "" },
        extraDetails: { type: Object, default: () => ({}) },
        drawingDelay: { type: Number, default: undefined },
    },
    setup(props) {
        const rdkitLoaded = ref(false);
        const rdkitError = ref(false);
        const wasCalled = ref(false);

        const molDetails = reactive({
            width: props.width,
            height: props.height,
            bondLineWidth: 1,
            addStereoAnnotation: true,
            ...props.extraDetails,
        });

        const svg = ref("");

        function isValid(m) {
            return !!m;
        }

        function isValidMolString(s) {
            // Check if RDKit is loaded before attempting validation
            if (!window.RDKit) {
                return false;
            }
            const mol = window.RDKit.get_mol(s || "invalid");
            const ok = isValid(mol);
            mol && mol.delete();
            return ok;
        }

        function getMolDetails(mol, qmol) {
            if (isValid(mol) && isValid(qmol)) {
                const matches = JSON.parse(
                    mol.get_substruct_matches(qmol) || "[]"
                );
                const merged = matches.reduce(
                    (acc, { atoms, bonds }) => ({
                        atoms: [...acc.atoms, ...atoms],
                        bonds: [...acc.bonds, ...bonds],
                    }),
                    { atoms: [], bonds: [] }
                );
                return JSON.stringify({
                    ...molDetails,
                    ...props.extraDetails,
                    ...merged,
                });
            }
            return JSON.stringify({ ...molDetails, ...props.extraDetails });
        }

        async function drawSVGorCanvas() {
            const mol = window.RDKit.get_mol(props.structure || "invalid");
            const qmol = window.RDKit.get_qmol(props.subStructure || "invalid");
            const valid = isValid(mol);

            if (props.svgMode && valid) {
                svg.value = mol.get_svg_with_highlights(
                    getMolDetails(mol, qmol)
                );
            } else if (valid) {
                await nextTick();
                const canvas = document.getElementById(props.id);
                mol.draw_to_canvas_with_highlights(
                    canvas,
                    getMolDetails(mol, qmol)
                );
            }

            mol && mol.delete();
            qmol && qmol.delete();
        }

        function draw() {
            if (props.drawingDelay) {
                setTimeout(drawSVGorCanvas, props.drawingDelay);
            } else {
                drawSVGorCanvas();
            }
        }

        function drawOnce() {
            if (!wasCalled.value) {
                wasCalled.value = true;
                draw();
            }
        }

        onMounted(() => {
            initRDKit()
                .then(() => {
                    rdkitLoaded.value = true;
                    draw();
                })
                .catch(() => {
                    rdkitError.value = true;
                });
        });

        onUpdated(() => {
            if (!rdkitError.value && rdkitLoaded.value && !props.svgMode) {
                drawOnce();
            }
        });

        watch(
            () => [
                props.structure,
                props.subStructure,
                props.svgMode,
                props.width,
                props.height,
            ],
            () => draw()
        );

        // Watch for RDKit to load and redraw if structure is already available
        watch(rdkitLoaded, (loaded) => {
            if (loaded && props.structure) {
                draw();
            }
        });

        return {
            rdkitLoaded,
            rdkitError,
            svg,
            isValidMolString,
        };
    },
};
</script>

<style>
.molecule-structure-svg svg rect:first-of-type {
    fill: transparent !important;
}
</style>
